import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiCopy } from "react-icons/fi";
import intl from "react-intl-universal";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import VerificationInput from "react-verification-input";
import { CompanyLogo } from "../../../Components/Atoms/CompanyLogo";
import { useCEPData } from "../../../Hooks/CEP";
import {
  getStateOptions,
  isBrCountry,
} from "../../Private/BaasConsole/Entidades/baasEntidadeCreateVisibility";
import {
  buildPhoneDialOptions,
  composeE164FromParts,
  createEmptyPhoneParts,
  digitsOnly,
  formatLocalPhoneNumberDisplay,
  formatPhoneDialOptionLabel,
  formatPhonePartsForDisplay,
  isPhonePartsComplete,
  splitE164ToParts,
} from "../../../Services/baasPhoneParts";
import {
  formatCountryOptionLabel,
  getFallbackCountryOptions,
} from "../../../Services/baasPlatformOptionsApi";
import {
  fetchPublicKycInviteStatus,
  isBaasKycNetworkError,
  sendPublicKycInviteEmailOtp,
  sendPublicKycInvitePhoneOtp,
  startPublicKycInviteVeriff,
  reconcilePublicKycInviteVeriff,
  submitPublicKycInviteProfile,
  verifyPublicKycInviteEmailOtp,
  verifyPublicKycInvitePhoneOtp,
} from "../../../Services/baasKycInvitePublicApi";
import { cepMask, cpfMask } from "../../../Utils/masks";

const STEPS = {
  LOADING: "loading",
  BLOCKED: "blocked",
  UNAVAILABLE: "unavailable",
  EMAIL: "email",
  EMAIL_CODE: "email_code",
  PHONE: "phone",
  PHONE_CODE: "phone_code",
  PERSONAL: "personal",
  ADDRESS: "address",
  REVIEW: "review",
  VERIFF: "veriff",
  DONE: "done",
};

/** HTTP statuses that mean the invite itself is gone / unusable (not a transport failure). */
const INVITE_GONE_STATUSES = new Set([404, 410, 403]);

const WIZARD_STEP_DEFS = {
  email: { id: "email", labelKey: "baas.kycOnboard.steps.email" },
  phone: { id: "phone", labelKey: "baas.kycOnboard.steps.phone" },
  personal: { id: "personal", labelKey: "baas.kycOnboard.steps.personal" },
  document: { id: "personal", labelKey: "baas.kycOnboard.steps.document" },
  address: { id: "address", labelKey: "baas.kycOnboard.steps.address" },
  veriff: { id: "veriff", labelKey: "baas.kycOnboard.steps.veriff" },
};

const parseInviteFlags = (payload) => {
  const isRevalidation = Boolean(payload?.is_revalidation ?? payload?.isRevalidation);
  const raw = payload?.update_scopes ?? payload?.updateScopes;
  const scopes = new Set(
    Array.isArray(raw) ? raw.map((item) => String(item || "").toLowerCase()) : [],
  );
  const includeLiveness = Boolean(
    payload?.include_liveness ?? payload?.includeLiveness ?? true,
  );
  return {
    isRevalidation,
    scopes,
    includeLiveness,
    needsPersonal: !isRevalidation || scopes.has("personal"),
    needsDocument: !isRevalidation || scopes.has("document"),
    needsAddress: !isRevalidation || scopes.has("address"),
    needsVeriff: !isRevalidation || includeLiveness,
  };
};

const buildWizardSteps = (flags) => {
  const steps = [WIZARD_STEP_DEFS.email, WIZARD_STEP_DEFS.phone];
  if (flags.isRevalidation && (flags.needsPersonal || flags.needsDocument)) {
    steps.push(flags.needsPersonal ? WIZARD_STEP_DEFS.personal : WIZARD_STEP_DEFS.document);
  }
  if (flags.isRevalidation && flags.needsAddress) steps.push(WIZARD_STEP_DEFS.address);
  if (flags.needsVeriff) steps.push(WIZARD_STEP_DEFS.veriff);
  return steps;
};

const emptyProfile = () => ({
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  motherName: "",
  cpf: "",
  addressLine1: "",
  addressNumber: "",
  addressNeighborhood: "",
  addressCity: "",
  addressState: "",
  addressPostalCode: "",
  addressCountry: "BR",
});

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const resolveVeriffUrl = (payload) =>
  String(
    payload?.url ||
      payload?.Url ||
      payload?.kyc_url ||
      payload?.kycUrl ||
      payload?.session_url ||
      payload?.sessionUrl ||
      "",
  ).trim();

const resolveVeriffSessionId = (payload) =>
  String(
    payload?.session_id ||
      payload?.sessionId ||
      payload?.veriff_session_id ||
      payload?.veriffSessionId ||
      "",
  ).trim();

const isValidCpf = (digits) => {
  if (!/^\d{11}$/.test(digits)) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  const calc = (base, factors) => {
    const sum = factors.reduce((acc, factor, i) => acc + Number(base[i]) * factor, 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(digits, [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calc(digits, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return digits.endsWith(`${d1}${d2}`);
};

const wizardIndexForStep = (step, wizardSteps, needsAddress) => {
  let id = null;
  if (step === STEPS.EMAIL || step === STEPS.EMAIL_CODE) id = "email";
  else if (step === STEPS.PHONE || step === STEPS.PHONE_CODE) id = "phone";
  else if (step === STEPS.PERSONAL) id = "personal";
  else if (step === STEPS.ADDRESS) id = "address";
  else if (step === STEPS.REVIEW) id = needsAddress ? "address" : "personal";
  else if (step === STEPS.VERIFF || step === STEPS.DONE) id = "veriff";
  if (!id) return -1;
  const index = wizardSteps.findIndex((item) => item.id === id);
  if (index >= 0) return index;
  if (step === STEPS.DONE) return wizardSteps.length - 1;
  return -1;
};

const phoneDigitsFromParts = (parts) => onlyDigits(composeE164FromParts(parts));

/**
 * Public blank onboard / KYC invite redeem page.
 * Route: `/auth/onboard/:token`
 * URL shape: `{carteira}/auth/onboard/{token}` — API via REACT_APP_BAAS_PUBLIC_URL.
 *
 * Steps: validate token → e-mail OTP → phone OTP → selected data (blank or revalidation) → Veriff if liveness.
 */
export const KycOnboardPage = () => {
  const { token } = useParams();
  const [step, setStep] = useState(STEPS.LOADING);
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [phoneParts, setPhoneParts] = useState(createEmptyPhoneParts);
  const [code, setCode] = useState("");
  const [profile, setProfile] = useState(emptyProfile);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [veriffUrl, setVeriffUrl] = useState("");
  const [veriffSessionId, setVeriffSessionId] = useState("");
  const [manualSessionId, setManualSessionId] = useState("");
  const [otpChannel, setOtpChannel] = useState("");
  const veriffStartRef = useRef(false);
  const [veriffLinkCopied, setVeriffLinkCopied] = useState(false);

  const { fetchCEPData, requestError: cepError, requestLoading: cepLoading, resetCEP } = useCEPData();

  const countryOptions = useMemo(() => getFallbackCountryOptions(), []);
  const phoneDialOptions = useMemo(() => buildPhoneDialOptions(countryOptions), [countryOptions]);
  const stateOptions = useMemo(
    () => getStateOptions(profile.addressCountry),
    [profile.addressCountry],
  );

  const inviteFlags = useMemo(() => parseInviteFlags(status), [status]);
  const {
    isRevalidation,
    needsPersonal,
    needsDocument,
    needsAddress,
  } = inviteFlags;
  const needsDataStep = needsPersonal || needsDocument;
  const wizardSteps = useMemo(() => buildWizardSteps(inviteFlags), [inviteFlags]);

  const title = useMemo(
    () =>
      isRevalidation
        ? intl.get("baas.kycOnboard.titleRevalidation") || intl.get("baas.kycOnboard.title")
        : intl.get("baas.kycOnboard.title") || "Onboarding",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRevalidation, intl.getInitOptions?.()?.currentLocale],
  );

  const applyPendingPhone = useCallback((rawPhone) => {
    if (!rawPhone) return;
    const parsed = splitE164ToParts(rawPhone);
    if (parsed) {
      setPhoneParts(parsed);
    }
  }, []);

  const applyStatus = useCallback(
    (payload) => {
      setStatus(payload);
      if (payload?.pending_email || payload?.pendingEmail) {
        setEmail(payload.pending_email || payload.pendingEmail);
      }
      if (payload?.pending_phone || payload?.pendingPhone) {
        applyPendingPhone(payload.pending_phone || payload.pendingPhone);
      }
      const next = String(payload?.next_step || payload?.nextStep || "").toLowerCase();
      const st = String(payload?.status || "").toLowerCase();
      if (st === "completed" || next === "done") {
        setStep(STEPS.DONE);
        return;
      }
      if (st === "revoked" || next === "blocked") {
        setStep(STEPS.BLOCKED);
        return;
      }
      if (st === "expired" && next !== "veriff") {
        setStep(STEPS.BLOCKED);
        return;
      }
      if (next === "email") {
        setStep(STEPS.EMAIL);
        return;
      }
      if (next === "phone") {
        setStep(STEPS.PHONE);
        return;
      }
      if (next === "profile") {
        const flags = parseInviteFlags(payload);
        if (flags.needsPersonal || flags.needsDocument) {
          setStep(STEPS.PERSONAL);
          return;
        }
        if (flags.needsAddress) {
          setStep(STEPS.ADDRESS);
          return;
        }
        setStep(flags.needsVeriff ? STEPS.VERIFF : STEPS.DONE);
        return;
      }
      if (next === "veriff") {
        setStep(STEPS.VERIFF);
        const existingUrl = resolveVeriffUrl(payload);
        if (existingUrl) setVeriffUrl(existingUrl);
        const existingSessionId = resolveVeriffSessionId(payload);
        if (existingSessionId) setVeriffSessionId(existingSessionId);
        return;
      }
      setStep(STEPS.EMAIL);
    },
    [applyPendingPhone],
  );

  const refresh = useCallback(async () => {
    if (!token) {
      setStep(STEPS.BLOCKED);
      setError(intl.get("baas.kycOnboard.invalidLink"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await fetchPublicKycInviteStatus(token);
      applyStatus(data);
    } catch (err) {
      if (isBaasKycNetworkError(err)) {
        setStep(STEPS.UNAVAILABLE);
        setError(intl.get("baas.kycOnboard.networkError"));
        return;
      }
      if (INVITE_GONE_STATUSES.has(err?.status)) {
        setStep(STEPS.BLOCKED);
        setError(err?.detail || intl.get("baas.kycOnboard.blocked"));
        return;
      }
      setStep(STEPS.UNAVAILABLE);
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.loadError"));
    } finally {
      setBusy(false);
    }
  }, [token, applyStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    resetCEP();
  }, [profile.addressCountry, resetCEP]);

  const updateProfile = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updatePhoneParts = (patch) => {
    setPhoneParts((prev) => ({ ...prev, ...patch }));
  };

  const tryFetchCep = (value) => {
    if (!isBrCountry(profile.addressCountry)) return;
    const digits = onlyDigits(value);
    if (digits.length !== 8) return;

    fetchCEPData({ cep: digits }).then((values) => {
      if (!values?.logradouro && !values?.localidade) return;
      setProfile((prev) => ({
        ...prev,
        addressLine1: values.logradouro || prev.addressLine1,
        addressNeighborhood: values.bairro || prev.addressNeighborhood,
        addressCity: values.localidade || prev.addressCity,
        addressState: values.uf || prev.addressState,
        addressPostalCode: values.cep || value,
        addressCountry: "BR",
      }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.addressLine1;
        delete next.addressCity;
        delete next.addressPostalCode;
        delete next.addressState;
        return next;
      });
    });
  };

  const validatePersonal = () => {
    const errors = {};
    if (needsPersonal) {
      if (!profile.firstName.trim()) errors.firstName = intl.get("baas.kycOnboard.validation.required");
      if (!profile.lastName.trim()) errors.lastName = intl.get("baas.kycOnboard.validation.required");
      if (!profile.dateOfBirth.trim()) {
        errors.dateOfBirth = intl.get("baas.kycOnboard.validation.required");
      }
      if (!profile.motherName.trim()) {
        errors.motherName = intl.get("baas.kycOnboard.validation.required");
      }
    }
    if (needsDocument) {
      const cpf = onlyDigits(profile.cpf);
      if (isBrCountry(profile.addressCountry) || !profile.addressCountry) {
        if (!isValidCpf(cpf)) errors.cpf = intl.get("baas.kycOnboard.validation.cpf");
      } else if (cpf.length < 5) {
        errors.cpf = intl.get("baas.kycOnboard.validation.required");
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAddress = () => {
    const errors = {};
    if (!profile.addressPostalCode.trim() && isBrCountry(profile.addressCountry)) {
      errors.addressPostalCode = intl.get("baas.kycOnboard.validation.required");
    }
    if (!profile.addressLine1.trim()) {
      errors.addressLine1 = intl.get("baas.kycOnboard.validation.required");
    }
    if (!profile.addressCity.trim()) {
      errors.addressCity = intl.get("baas.kycOnboard.validation.required");
    }
    if (!profile.addressCountry.trim()) {
      errors.addressCountry = intl.get("baas.kycOnboard.validation.required");
    }
    if (stateOptions.length > 0 && !profile.addressState.trim()) {
      errors.addressState = intl.get("baas.kycOnboard.validation.required");
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFullProfile = () => {
    const personalOk = needsDataStep ? validatePersonal() : true;
    const addressOk = needsAddress ? validateAddress() : true;
    return personalOk && addressOk;
  };

  const handleSendEmail = async (event) => {
    event.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(intl.get("baas.kycOnboard.validation.email"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await sendPublicKycInviteEmailOtp(token, trimmed);
      setCode("");
      setOtpChannel("email");
      setStep(STEPS.EMAIL_CODE);
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.sendError"));
    } finally {
      setBusy(false);
    }
  };

  const canSubmitOtp = !busy && code.length >= 6;

  const handleVerifyEmail = async (event) => {
    event.preventDefault();
    if (!canSubmitOtp) return;
    setBusy(true);
    setError("");
    try {
      await verifyPublicKycInviteEmailOtp(token, code);
      setCode("");
      await refresh();
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.verifyError"));
    } finally {
      setBusy(false);
    }
  };

  const handleSendPhone = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!isPhonePartsComplete(phoneParts)) {
      setError(intl.get("baas.kycOnboard.validation.phone"));
      return;
    }
    const digits = phoneDigitsFromParts(phoneParts);
    if (digits.length < 10 || digits.length > 15) {
      setError(intl.get("baas.kycOnboard.validation.phone"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await sendPublicKycInvitePhoneOtp(token, digits);
      setCode("");
      setOtpChannel(data?.channel || "sms");
      setStep(STEPS.PHONE_CODE);
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.sendError"));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyPhone = async (event) => {
    event.preventDefault();
    if (!canSubmitOtp) return;
    setBusy(true);
    setError("");
    try {
      await verifyPublicKycInvitePhoneOtp(token, code);
      setCode("");
      await refresh();
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.verifyError"));
    } finally {
      setBusy(false);
    }
  };

  const handlePersonalContinue = (event) => {
    event.preventDefault();
    if (!validatePersonal()) {
      setError(intl.get("baas.kycOnboard.validation.form"));
      return;
    }
    setError("");
    setStep(needsAddress ? STEPS.ADDRESS : STEPS.REVIEW);
  };

  const handleAddressContinue = (event) => {
    event.preventDefault();
    if (!validateAddress()) {
      setError(intl.get("baas.kycOnboard.validation.form"));
      return;
    }
    setError("");
    setStep(STEPS.REVIEW);
  };

  const handleSubmitProfile = async () => {
    if (busy) return;
    if (!validateFullProfile()) {
      setStep(needsDataStep ? STEPS.PERSONAL : STEPS.ADDRESS);
      setError(intl.get("baas.kycOnboard.validation.form"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        first_name: profile.firstName.trim() || undefined,
        last_name: profile.lastName.trim() || undefined,
        date_of_birth: profile.dateOfBirth.trim() || undefined,
        mother_name: needsPersonal ? profile.motherName.trim() : undefined,
        email: email.trim() || undefined,
        phone_number: phoneDigitsFromParts(phoneParts) || undefined,
        national_id: onlyDigits(profile.cpf)
          ? {
              number: onlyDigits(profile.cpf),
              country_code: profile.addressCountry.trim().toUpperCase() || "BR",
            }
          : undefined,
        address: needsAddress
          ? {
              line_1: profile.addressLine1.trim(),
              number: profile.addressNumber.trim() || undefined,
              neighborhood: profile.addressNeighborhood.trim() || undefined,
              city: profile.addressCity.trim(),
              state: profile.addressState.trim() || undefined,
              postal_code: profile.addressPostalCode.trim() || undefined,
              country_code: profile.addressCountry.trim().toUpperCase(),
            }
          : undefined,
      };
      const data = await submitPublicKycInviteProfile(token, payload);
      applyStatus(data);
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.profileError"));
    } finally {
      setBusy(false);
    }
  };

  const handleStartVeriff = async () => {
    if (busy) return;
    if (veriffUrl) {
      window.open(veriffUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await startPublicKycInviteVeriff(token);
      const url = resolveVeriffUrl(data);
      const sessionId = resolveVeriffSessionId(data);
      if (url) setVeriffUrl(url);
      if (sessionId) setVeriffSessionId(sessionId);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      await refresh();
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.veriffError"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step !== STEPS.VERIFF || veriffUrl || !token || veriffStartRef.current) return;
    veriffStartRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const data = await startPublicKycInviteVeriff(token);
        const url = resolveVeriffUrl(data);
        const sessionId = resolveVeriffSessionId(data);
        if (cancelled) return;
        if (url) {
          setVeriffUrl(url);
        } else {
          veriffStartRef.current = false;
        }
        if (sessionId) setVeriffSessionId(sessionId);
      } catch (err) {
        if (!cancelled) {
          veriffStartRef.current = false;
          setError(err?.detail || err?.message || intl.get("baas.kycOnboard.veriffError"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, token, veriffUrl]);

  const handleReconcileVeriff = async (sessionIdOverride) => {
    const sessionId = String(sessionIdOverride || veriffSessionId || manualSessionId || "").trim();
    if (!sessionId || busy || !token) return;
    setBusy(true);
    setError("");
    try {
      const data = await reconcilePublicKycInviteVeriff(token, sessionId);
      setVeriffSessionId(sessionId);
      applyStatus(data);
    } catch (err) {
      setError(err?.detail || err?.message || intl.get("baas.kycOnboard.veriffError"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step !== STEPS.VERIFF || !token) return undefined;
    const sessionId = veriffSessionId || resolveVeriffSessionId(status);
    if (!sessionId) return undefined;

    const timer = window.setInterval(() => {
      handleReconcileVeriff(sessionId).catch(() => {});
    }, 15000);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, token, veriffSessionId, status?.status, status?.next_step]);

  const handleCopyVeriffUrl = async () => {
    if (!veriffUrl) return;
    try {
      await navigator.clipboard.writeText(veriffUrl);
      setVeriffLinkCopied(true);
      window.setTimeout(() => setVeriffLinkCopied(false), 2000);
    } catch {
      setError(intl.get("baas.kycOnboard.copyVeriffLinkError"));
    }
  };

  const activeWizard = wizardIndexForStep(step, wizardSteps, needsAddress);
  const showWizard = activeWizard >= 0;
  const dial = digitsOnly(phoneParts.countryDialCode) || "55";
  const localPhoneDisplay = formatLocalPhoneNumberDisplay(phoneParts.number, dial);

  return (
    <div className="kyc-onboard">
      <div className="kyc-onboard__logo" aria-label="Valut">
        <CompanyLogo forceValut onDark variant="gold" width={168} height={32} />
      </div>

      <div className="kyc-onboard__card">
        <header className="kyc-onboard__header">
          <h1 className="kyc-onboard__title">{title}</h1>
          <p className="kyc-onboard__subtitle">
            {isRevalidation
              ? intl.get("baas.kycOnboard.subtitleRevalidation")
              : intl.get("baas.kycOnboard.subtitle")}
          </p>
        </header>

        {showWizard ? (
          <ol className="kyc-onboard__steps" aria-label={intl.get("baas.kycOnboard.stepsLabel")}>
            {wizardSteps.map((item, index) => {
              const done = index < activeWizard || step === STEPS.DONE;
              const current = index === activeWizard && step !== STEPS.DONE;
              return (
                <li
                  key={item.id}
                  className={`kyc-onboard__step${
                    current ? " kyc-onboard__step--current" : done ? " kyc-onboard__step--done" : ""
                  }`}
                >
                  <span className="kyc-onboard__step-index">{index + 1}</span>
                  <span className="kyc-onboard__step-label">{intl.get(item.labelKey)}</span>
                </li>
              );
            })}
          </ol>
        ) : null}

        {error ? (
          <p className="kyc-onboard__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="kyc-onboard__body">
          {step === STEPS.LOADING ? (
            <p className="kyc-onboard__hint">{intl.get("baas.kycOnboard.loading")}</p>
          ) : null}

          {step === STEPS.BLOCKED ? (
            <p className="kyc-onboard__hint">{intl.get("baas.kycOnboard.blocked")}</p>
          ) : null}

          {step === STEPS.UNAVAILABLE ? (
            <div className="kyc-onboard__stack">
              <p className="kyc-onboard__hint">{intl.get("baas.kycOnboard.unavailableHint")}</p>
              <button
                type="button"
                onClick={() => refresh()}
                disabled={busy}
                className="kyc-onboard__btn kyc-onboard__btn--secondary"
              >
                {intl.get("baas.kycOnboard.refreshStatus")}
              </button>
            </div>
          ) : null}

          {step === STEPS.DONE ? (
            <p className="kyc-onboard__success">{intl.get("baas.kycOnboard.completed")}</p>
          ) : null}

          {step === STEPS.EMAIL ? (
            <form className="kyc-onboard__stack" onSubmit={handleSendEmail}>
              <p className="kyc-onboard__hint kyc-onboard__hint--muted">
                {intl.get("baas.kycOnboard.emailStepHint")}
              </p>
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.emailLabel")}</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="kyc-onboard__input"
                  autoComplete="email"
                />
              </label>
              <button type="submit" disabled={busy} className="kyc-onboard__btn">
                {busy
                  ? intl.get("baas.kycOnboard.sending")
                  : intl.get("baas.kycOnboard.sendEmailCode")}
              </button>
            </form>
          ) : null}

          {step === STEPS.EMAIL_CODE || step === STEPS.PHONE_CODE ? (
            <form
              className="kyc-onboard__stack kyc-onboard__otp-form"
              onSubmit={step === STEPS.EMAIL_CODE ? handleVerifyEmail : handleVerifyPhone}
            >
              <p className="kyc-onboard__hint kyc-onboard__otp-hint">
                {intl.get("baas.kycOnboard.codeHint", {
                  channel: otpChannel || (step === STEPS.EMAIL_CODE ? "email" : "sms"),
                })}
              </p>
              <div className="login-verification-otp">
                <VerificationInput
                  length={6}
                  value={code}
                  onChange={setCode}
                  autoFocus
                  placeholder=""
                  validChars="0-9"
                  classNames={{
                    container: "login-verification-otp__container",
                    character: "login-verification-otp__char",
                    characterInactive: "login-verification-otp__char--inactive",
                    characterSelected: "login-verification-otp__char--selected",
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                    onKeyDown: (event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      event.stopPropagation();
                      if (!canSubmitOtp) return;
                      if (step === STEPS.EMAIL_CODE) {
                        handleVerifyEmail(event);
                      } else {
                        handleVerifyPhone(event);
                      }
                    },
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!canSubmitOtp}
                className="kyc-onboard__btn"
              >
                {busy
                  ? intl.get("baas.kycOnboard.verifying")
                  : intl.get("baas.kycOnboard.verifyCode")}
              </button>
            </form>
          ) : null}

          {step === STEPS.PHONE ? (
            <form className="kyc-onboard__stack" onSubmit={handleSendPhone}>
              <p className="kyc-onboard__hint kyc-onboard__hint--muted">
                {intl.get("baas.kycOnboard.phoneStepHint")}
              </p>
              <div className="kyc-onboard__phone-grid">
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">
                    {intl.get("baas.entidades.personal.phoneCountry")}
                  </span>
                  <select
                    className="kyc-onboard__input"
                    value={phoneParts.countryDialCode || ""}
                    onChange={(e) => updatePhoneParts({ countryDialCode: digitsOnly(e.target.value) })}
                    required
                  >
                    <option value="" disabled>
                      {intl.get("baas.entidades.personal.selectPhoneCountry")}
                    </option>
                    {phoneDialOptions.map((option) => (
                      <option key={`${option.dial}-${option.code}`} value={option.dial}>
                        {formatPhoneDialOptionLabel(option)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">
                    {intl.get("baas.entidades.personal.phoneAreaCode")}
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={phoneParts.areaCode || ""}
                    onChange={(e) =>
                      updatePhoneParts({ areaCode: digitsOnly(e.target.value).slice(0, 4) })
                    }
                    className="kyc-onboard__input"
                    placeholder="11"
                    autoComplete="tel-area-code"
                  />
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">
                    {intl.get("baas.entidades.personal.phoneLocalNumber")}
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={localPhoneDisplay}
                    onChange={(e) => {
                      const maxLen = dial === "55" ? 9 : 12;
                      updatePhoneParts({ number: digitsOnly(e.target.value).slice(0, maxLen) });
                    }}
                    className="kyc-onboard__input"
                    placeholder={dial === "55" ? "99999-9999" : "999990000"}
                    autoComplete="tel-national"
                  />
                </label>
              </div>
              <button type="submit" disabled={busy} className="kyc-onboard__btn">
                {busy
                  ? intl.get("baas.kycOnboard.sending")
                  : intl.get("baas.kycOnboard.sendPhoneCode")}
              </button>
            </form>
          ) : null}

          {step === STEPS.PERSONAL ? (
            <form className="kyc-onboard__stack kyc-onboard__stack--tight" onSubmit={handlePersonalContinue}>
              <p className="kyc-onboard__hint kyc-onboard__hint--muted">
                {isRevalidation
                  ? intl.get("baas.kycOnboard.personalStepHintRevalidation")
                  : intl.get("baas.kycOnboard.personalStepHint")}
              </p>
              {needsPersonal ? (
                <>
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.firstName")}</span>
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => updateProfile("firstName", e.target.value)}
                  className="kyc-onboard__input"
                  autoComplete="given-name"
                />
                {fieldErrors.firstName ? (
                  <span className="kyc-onboard__field-error">{fieldErrors.firstName}</span>
                ) : null}
              </label>
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.lastName")}</span>
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => updateProfile("lastName", e.target.value)}
                  className="kyc-onboard__input"
                  autoComplete="family-name"
                />
                {fieldErrors.lastName ? (
                  <span className="kyc-onboard__field-error">{fieldErrors.lastName}</span>
                ) : null}
              </label>
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.dateOfBirth")}</span>
                <input
                  type="date"
                  value={profile.dateOfBirth}
                  onChange={(e) => updateProfile("dateOfBirth", e.target.value)}
                  className="kyc-onboard__input"
                  autoComplete="bday"
                />
                {fieldErrors.dateOfBirth ? (
                  <span className="kyc-onboard__field-error">{fieldErrors.dateOfBirth}</span>
                ) : null}
              </label>
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.motherName")}</span>
                <input
                  type="text"
                  required
                  value={profile.motherName}
                  onChange={(e) => updateProfile("motherName", e.target.value)}
                  className="kyc-onboard__input"
                  autoComplete="off"
                />
                {fieldErrors.motherName ? (
                  <span className="kyc-onboard__field-error">{fieldErrors.motherName}</span>
                ) : null}
              </label>
                </>
              ) : null}
              {needsDocument ? (
              <label className="kyc-onboard__field">
                <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.cpf")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={
                    isBrCountry(profile.addressCountry) || !profile.addressCountry
                      ? cpfMask(profile.cpf)
                      : profile.cpf
                  }
                  onChange={(e) => {
                    const next = isBrCountry(profile.addressCountry) || !profile.addressCountry
                      ? onlyDigits(e.target.value).slice(0, 11)
                      : e.target.value;
                    updateProfile("cpf", next);
                  }}
                  className="kyc-onboard__input"
                />
                {fieldErrors.cpf ? (
                  <span className="kyc-onboard__field-error">{fieldErrors.cpf}</span>
                ) : null}
              </label>
              ) : null}
              <button type="submit" disabled={busy} className="kyc-onboard__btn">
                {needsAddress
                  ? intl.get("baas.kycOnboard.continueAddress")
                  : intl.get("baas.kycOnboard.continueReview")}
              </button>
            </form>
          ) : null}

          {step === STEPS.ADDRESS ? (
            <form className="kyc-onboard__stack kyc-onboard__stack--tight" onSubmit={handleAddressContinue}>
              <p className="kyc-onboard__hint kyc-onboard__hint--muted">
                {intl.get("baas.kycOnboard.addressStepHint")}
              </p>
              <div className="kyc-onboard__address-grid">
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.postalCode")}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={
                      isBrCountry(profile.addressCountry)
                        ? cepMask(profile.addressPostalCode)
                        : profile.addressPostalCode
                    }
                    onChange={(e) => {
                      const raw = isBrCountry(profile.addressCountry)
                        ? onlyDigits(e.target.value).slice(0, 8)
                        : e.target.value;
                      updateProfile("addressPostalCode", raw);
                      tryFetchCep(raw);
                    }}
                    onBlur={() => tryFetchCep(profile.addressPostalCode)}
                    className="kyc-onboard__input"
                    autoComplete="postal-code"
                  />
                  {cepLoading ? (
                    <span className="kyc-onboard__hint kyc-onboard__hint--muted">
                      {intl.get("baas.entidades.create.cepLoading")}
                    </span>
                  ) : null}
                  {cepError ? (
                    <span className="kyc-onboard__field-error">
                      {intl.get("baas.entidades.create.cepError")}
                    </span>
                  ) : null}
                  {fieldErrors.addressPostalCode ? (
                    <span className="kyc-onboard__field-error">{fieldErrors.addressPostalCode}</span>
                  ) : null}
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.country")}</span>
                  <select
                    className="kyc-onboard__input"
                    value={profile.addressCountry}
                    onChange={(e) => updateProfile("addressCountry", e.target.value)}
                    required
                  >
                    <option value="">
                      {intl.get("baas.entidades.personal.selectCountry")}
                    </option>
                    {countryOptions.map((country) => (
                      <option key={country.code} value={country.code}>
                        {formatCountryOptionLabel(country)}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.addressCountry ? (
                    <span className="kyc-onboard__field-error">{fieldErrors.addressCountry}</span>
                  ) : null}
                </label>
                <label className="kyc-onboard__field kyc-onboard__field--span2">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.addressLine1")}</span>
                  <input
                    type="text"
                    value={profile.addressLine1}
                    onChange={(e) => updateProfile("addressLine1", e.target.value)}
                    className="kyc-onboard__input"
                    autoComplete="address-line1"
                  />
                  {fieldErrors.addressLine1 ? (
                    <span className="kyc-onboard__field-error">{fieldErrors.addressLine1}</span>
                  ) : null}
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.addressNumber")}</span>
                  <input
                    type="text"
                    value={profile.addressNumber}
                    onChange={(e) => updateProfile("addressNumber", e.target.value)}
                    className="kyc-onboard__input"
                  />
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.neighborhood")}</span>
                  <input
                    type="text"
                    value={profile.addressNeighborhood}
                    onChange={(e) => updateProfile("addressNeighborhood", e.target.value)}
                    className="kyc-onboard__input"
                  />
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.city")}</span>
                  <input
                    type="text"
                    value={profile.addressCity}
                    onChange={(e) => updateProfile("addressCity", e.target.value)}
                    className="kyc-onboard__input"
                    autoComplete="address-level2"
                  />
                  {fieldErrors.addressCity ? (
                    <span className="kyc-onboard__field-error">{fieldErrors.addressCity}</span>
                  ) : null}
                </label>
                <label className="kyc-onboard__field">
                  <span className="kyc-onboard__label">{intl.get("baas.kycOnboard.fields.state")}</span>
                  {stateOptions.length > 0 ? (
                    <select
                      className="kyc-onboard__input"
                      value={profile.addressState}
                      onChange={(e) => updateProfile("addressState", e.target.value)}
                    >
                      <option value="">{intl.get("baas.entidades.create.selectState")}</option>
                      {stateOptions.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={profile.addressState}
                      onChange={(e) => updateProfile("addressState", e.target.value)}
                      className="kyc-onboard__input"
                      autoComplete="address-level1"
                    />
                  )}
                  {fieldErrors.addressState ? (
                    <span className="kyc-onboard__field-error">{fieldErrors.addressState}</span>
                  ) : null}
                </label>
              </div>
              <button type="submit" disabled={busy} className="kyc-onboard__btn">
                {intl.get("baas.kycOnboard.continueReview")}
              </button>
              {needsDataStep ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setStep(STEPS.PERSONAL)}
                className="kyc-onboard__btn kyc-onboard__btn--ghost"
              >
                {intl.get("baas.kycOnboard.backToPersonal")}
              </button>
              ) : null}
            </form>
          ) : null}

          {step === STEPS.REVIEW ? (
            <div className="kyc-onboard__stack">
              <p className="kyc-onboard__hint">
                {isRevalidation
                  ? intl.get("baas.kycOnboard.reviewHintRevalidation")
                  : intl.get("baas.kycOnboard.reviewHint")}
              </p>
              <dl className="kyc-onboard__review">
                <div>
                  <dt>{intl.get("baas.kycOnboard.emailLabel")}</dt>
                  <dd>{email}</dd>
                </div>
                <div>
                  <dt>{intl.get("baas.kycOnboard.phoneLabel")}</dt>
                  <dd>{formatPhonePartsForDisplay(phoneParts)}</dd>
                </div>
                {needsPersonal ? (
                <>
                <div>
                  <dt>{intl.get("baas.kycOnboard.fields.firstName")}</dt>
                  <dd>
                    {profile.firstName} {profile.lastName}
                  </dd>
                </div>
                <div>
                  <dt>{intl.get("baas.kycOnboard.fields.motherName")}</dt>
                  <dd>{profile.motherName}</dd>
                </div>
                </>
                ) : null}
                {needsDocument ? (
                <div>
                  <dt>{intl.get("baas.kycOnboard.fields.cpf")}</dt>
                  <dd>
                    {isBrCountry(profile.addressCountry)
                      ? cpfMask(profile.cpf)
                      : profile.cpf}
                  </dd>
                </div>
                ) : null}
                {needsAddress ? (
                <div>
                  <dt>{intl.get("baas.kycOnboard.fields.addressLine1")}</dt>
                  <dd>
                    {[
                      profile.addressLine1,
                      profile.addressNumber,
                      profile.addressNeighborhood,
                      profile.addressCity,
                      profile.addressState,
                      profile.addressPostalCode,
                      profile.addressCountry,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </dd>
                </div>
                ) : null}
              </dl>
              <button
                type="button"
                disabled={busy}
                onClick={handleSubmitProfile}
                className="kyc-onboard__btn"
              >
                {busy
                  ? intl.get("baas.kycOnboard.submitting")
                  : isRevalidation
                    ? intl.get("baas.kycOnboard.submitUpdate")
                    : intl.get("baas.kycOnboard.submitProfile")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setStep(needsAddress ? STEPS.ADDRESS : STEPS.PERSONAL)}
                className="kyc-onboard__btn kyc-onboard__btn--ghost"
              >
                {intl.get("baas.kycOnboard.backToForm")}
              </button>
            </div>
          ) : null}

          {step === STEPS.VERIFF ? (
            <div className="kyc-onboard__stack">
              <p className="kyc-onboard__hint kyc-onboard__hint--center">
                {intl.get("baas.kycOnboard.veriffHintRevalidation")}
              </p>
              {veriffUrl ? (
                <div className="kyc-onboard__veriff">
                  <div className="kyc-onboard__qr" aria-hidden="true">
                    <QRCode value={veriffUrl} size={96} bgColor="#ffffff" fgColor="#0a0a0e" />
                  </div>
                  <div className="kyc-onboard__veriff-aside">
                    <div className="kyc-onboard__veriff-link-row">
                      <a
                        href={veriffUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kyc-onboard__veriff-url"
                      >
                        {veriffUrl}
                      </a>
                      <button
                        type="button"
                        className="kyc-onboard__copy"
                        onClick={handleCopyVeriffUrl}
                        title={intl.get("baas.kycOnboard.copyVeriffLink")}
                        aria-label={
                          veriffLinkCopied
                            ? intl.get("baas.kycOnboard.copiedVeriffLink")
                            : intl.get("baas.kycOnboard.copyVeriffLink")
                        }
                      >
                        {veriffLinkCopied ? (
                          <FiCheck aria-hidden size={16} />
                        ) : (
                          <FiCopy aria-hidden size={16} />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleStartVeriff}
                      className="kyc-onboard__btn"
                    >
                      {busy
                        ? intl.get("baas.kycOnboard.openingVeriff")
                        : intl.get("baas.kycOnboard.openVeriff")}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={refresh}
                      className="kyc-onboard__btn kyc-onboard__btn--ghost"
                    >
                      {intl.get("baas.kycOnboard.refreshStatus")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={handleStartVeriff}
                    className="kyc-onboard__btn"
                  >
                    {busy
                      ? intl.get("baas.kycOnboard.openingVeriff")
                      : intl.get("baas.kycOnboard.openVeriff")}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={refresh}
                    className="kyc-onboard__btn kyc-onboard__btn--ghost"
                  >
                    {intl.get("baas.kycOnboard.refreshStatus")}
                  </button>
                </>
              )}
              <div className="kyc-onboard__stack">
                <label className="kyc-onboard__label" htmlFor="veriff-session-id">
                  Session ID Veriff (se já concluiu fora do link)
                </label>
                <input
                  id="veriff-session-id"
                  type="text"
                  className="kyc-onboard__input"
                  value={manualSessionId}
                  onChange={(event) => setManualSessionId(event.target.value)}
                  placeholder="0ca3c197-1712-4542-9651-5685065c8908"
                />
                <button
                  type="button"
                  disabled={busy || (!manualSessionId.trim() && !veriffSessionId)}
                  onClick={() => handleReconcileVeriff()}
                  className="kyc-onboard__btn kyc-onboard__btn--ghost"
                >
                  Sincronizar Veriff
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default KycOnboardPage;
